import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { StatusChip } from "@/components/StatusChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus } from "lucide-react";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "incharge" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/employees").then((r) => setEmployees(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/employees", f);
      toast.success("Employee login created");
      setF({ name: "", email: "", password: "", role: "incharge" });
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggle = async (emp) => {
    try {
      const { data } = await api.patch(`/employees/${emp.id}/toggle`);
      toast.success(`${emp.name} ${data.active ? "activated" : "deactivated"}`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-5" data-testid="employees-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Employees</h1>

      <form onSubmit={create} className="card-tactical p-5 space-y-4" data-testid="create-employee-form">
        <h2 className="font-heading text-lg font-semibold">Create Employee Login</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Input data-testid="emp-name" required placeholder="Full name" value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-slate-50 border-border" />
          <Input data-testid="emp-email" required type="email" placeholder="Email" value={f.email}
            onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-slate-50 border-border" />
          <Input data-testid="emp-password" required type="password" placeholder="Password" value={f.password}
            onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-slate-50 border-border" />
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger className="bg-slate-50 border-border" data-testid="emp-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="incharge">Swap Station Incharge</SelectItem>
              <SelectItem value="ops">Ops / Field Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" disabled={busy} data-testid="create-employee-submit"
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold">
          <UserPlus className="h-4 w-4 mr-2" /> {busy ? "Creating…" : "Create Login"}
        </Button>
      </form>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-border">
            <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th>
            <th className="p-3">Status</th><th className="p-3">Action</th>
          </tr></thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id} className="border-b border-border/50 hover:bg-slate-50" data-testid={`employee-row-${emp.email}`}>
                <td className="p-3 font-medium">{emp.name}</td>
                <td className="p-3 font-mono text-xs">{emp.email}</td>
                <td className="p-3"><StatusChip label={emp.role === "incharge" ? "Swap Incharge" : "Ops Staff"} tone="info" testid={`emp-role-chip-${emp.email}`} /></td>
                <td className="p-3"><StatusChip label={emp.active ? "Active" : "Deactivated"} tone={emp.active ? "paid" : "overdue"} testid={`emp-status-${emp.email}`} /></td>
                <td className="p-3">
                  <Button size="sm" variant="outline" className="h-7 text-xs border-border"
                    data-testid={`emp-toggle-${emp.email}`} onClick={() => toggle(emp)}>
                    {emp.active ? "Deactivate" : "Activate"}
                  </Button>
                </td>
              </tr>
            ))}
            {employees.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-500" data-testid="employees-empty">No employee logins yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
